/**
 * Retail > Revenue Leakage (/retail/leakage).
 *
 * A category- and SKU-level explorer that ranks OMNI merchandise categories by
 * their dollar decline in sales versus the same trailing window last year, so
 * the biggest revenue losses surface at the top. A months selector chooses the
 * trailing window (default 8); each option names the actual calendar range it
 * covers (e.g. "8 months (Dec 2025 - Jul 2026)"), and a note below it names the
 * current in-progress month the trailing window deliberately excludes. The
 * category table lists category, this-year sales, last-year sales, YoY change,
 * and comp %. YoY change is the dollar move versus last year: growth (this year
 * above last year) shows as a positive figure, decline (this year below last
 * year) shows as a negative one, both colored via compColorClass so up is green
 * and down is red, consistent with the rest of the OMNI report. Rows still rank
 * by largest dollar decline first, that ranking is unchanged. When a row has no
 * last-year sales to compare against (comp is null), the YoY change and comp %
 * cells read "No LY sales" instead of a number, on both the row and the total.
 * Clicking a category drills to the SKUs inside it (item + description +
 * subcategory, same metric columns); a back control returns to the category
 * list. An Excel export mirrors the other retail exports and follows the
 * current view.
 *
 * Categories are leadership's product taxonomy, mirrored from CounterPoint (see
 * useLeakage.ts). A category row's name is also its drill token, so there is no
 * code-to-name lookup in this page.
 *
 * RBAC: access requires retail.omni.read (fleet) or stores:store (own-store),
 * mirroring the OMNI Performance page. Scope is enforced server-side, so the
 * page renders whatever the API returns.
 *
 * Resilience: the backend endpoint ships in parallel (api repo), so the page is
 * wrapped in an ErrorBoundary, reads every field defensively, and renders a
 * quiet "not available yet" state on any 404 / 503 / shape mismatch instead of
 * crashing, the same posture as the other tools built before their backend.
 */

import { useState } from 'react';
import { ArrowLeft, ChevronRight, Download, Loader2 } from 'lucide-react';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { FilterLabel } from '@/components/FilterLabel';
import { gridTheadClassName } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { compColorClass } from '@/lib/compColor';
import { useSession } from '@/lib/useSession';
import { formatComp, formatDollars, formatWeekDate } from '@/lib/useRetailStores';
import {
  useLeakageCategories,
  useLeakageSkus,
  downloadLeakageXlsx,
  DEFAULT_LEAKAGE_MONTHS,
  LEAKAGE_MONTHS_OPTIONS,
  type LeakageCategory,
  type LeakageSku,
} from '@/lib/useLeakage';
import {
  leakageMonthsOptionLabel,
  excludedMonthLabel,
  formatYoyChangeAmount,
  NO_LY_SALES_LABEL,
} from '@/lib/leakageLabels';

// ---------------------------------------------------------------------------
// RBAC
// ---------------------------------------------------------------------------

/**
 * Fleet users hold stores.leakage (its own accessSlug); a store manager holds
 * stores.sweetspot and gets an own-store view. Either grants access; the
 * backend enforces which stores actually come back.
 */
function canViewLeakage(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  if (!perms || perms.length === 0) return false;
  return perms.includes('stores.leakage') || perms.includes('stores.sweetspot');
}

// ---------------------------------------------------------------------------
// Presentational atoms
// ---------------------------------------------------------------------------

function AccessDeniedCard() {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-8 text-center dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="text-sm font-medium text-amp-ink dark:text-amp-dark-ink">
        Revenue Leakage is gated by the retail.omni.read capability.
      </p>
      <p className="mt-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">Ask IT to grant you access.</p>
    </div>
  );
}

function NotAvailableCard() {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-8 text-center dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="text-sm font-medium text-amp-ink dark:text-amp-dark-ink">Not available yet.</p>
      <p className="mt-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
        The Revenue Leakage report is being built. It will populate here once the leakage feed is live.
      </p>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Grid
// ---------------------------------------------------------------------------

/** The four metric columns shared by the category and SKU tables. */
const METRIC_HEADERS = ['TY sales', 'LY sales', 'YoY change', 'Comp %'] as const;

function MetricCells({
  ty,
  ly,
  decline,
  comp,
}: {
  ty: number | null;
  ly: number | null;
  decline: number | null;
  comp: number | null;
}) {
  // A null comp means there is no last-year baseline to compare against
  // (ly is 0, or either side is missing), so the change and comp cells read
  // "No LY sales" instead of a number that would otherwise read as a loss of
  // zero or, once the LY backfill lands, an outsized false decline/growth.
  const noLyBasis = comp === null;
  const changeAndCompClass = noLyBasis
    ? 'text-amp-ink-muted dark:text-amp-dark-ink-muted'
    : compColorClass(comp);
  return (
    <>
      <td className="px-3 py-2 text-right whitespace-nowrap text-amp-ink dark:text-amp-dark-ink">
        {formatDollars(ty)}
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap text-amp-ink dark:text-amp-dark-ink">
        {formatDollars(ly)}
      </td>
      <td className={`px-3 py-2 text-right whitespace-nowrap font-medium ${changeAndCompClass}`}>
        {noLyBasis ? NO_LY_SALES_LABEL : formatYoyChangeAmount(decline)}
      </td>
      <td className={`px-3 py-2 text-right whitespace-nowrap ${changeAndCompClass}`}>
        {noLyBasis ? NO_LY_SALES_LABEL : formatComp(comp)}
      </td>
    </>
  );
}

function MetricHead() {
  return (
    <>
      {METRIC_HEADERS.map((h) => (
        <th key={h} className="px-3 py-2 text-right font-medium whitespace-nowrap">
          {h}
        </th>
      ))}
    </>
  );
}

function EmptyBody({ colSpan, message }: { colSpan: number; message: string }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
        {message}
      </td>
    </tr>
  );
}

function CategoryTable({
  rows,
  isLoading,
  error,
  onDrill,
}: {
  rows: LeakageCategory[];
  isLoading: boolean;
  error: Error | null;
  onDrill: (category: string) => void;
}) {
  const colSpan = 1 + METRIC_HEADERS.length;
  return (
    <table className="min-w-full text-sm">
      <thead className={gridTheadClassName}>
        <tr>
          <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Category</th>
          <MetricHead />
        </tr>
      </thead>
      <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
        {isLoading ? (
          <EmptyBody colSpan={colSpan} message="Loading..." />
        ) : error ? (
          <EmptyBody colSpan={colSpan} message="Not available yet." />
        ) : rows.length === 0 ? (
          <EmptyBody colSpan={colSpan} message="No categories to show for this window." />
        ) : (
          rows.map((r) => (
            <tr
              key={r.category}
              onClick={() => onDrill(r.category)}
              className="cursor-pointer hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm"
              title={`View SKUs in ${r.category}`}
            >
              <td className="px-3 py-2 whitespace-nowrap text-amp-ink dark:text-amp-dark-ink">
                <span className="inline-flex items-center gap-1">
                  <ChevronRight className="h-3 w-3 text-amp-ink-muted dark:text-amp-dark-ink-muted" aria-hidden />
                  {r.category}
                </span>
              </td>
              <MetricCells ty={r.ty} ly={r.ly} decline={r.decline} comp={r.comp} />
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

function SkuTable({
  rows,
  isLoading,
  error,
}: {
  rows: LeakageSku[];
  isLoading: boolean;
  error: Error | null;
}) {
  const colSpan = 2 + METRIC_HEADERS.length;
  return (
    <table className="min-w-full text-sm">
      <thead className={gridTheadClassName}>
        <tr>
          <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Item</th>
          <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Subcategory</th>
          <MetricHead />
        </tr>
      </thead>
      <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
        {isLoading ? (
          <EmptyBody colSpan={colSpan} message="Loading..." />
        ) : error ? (
          <EmptyBody colSpan={colSpan} message="Not available yet." />
        ) : rows.length === 0 ? (
          <EmptyBody colSpan={colSpan} message="No SKUs to show for this category." />
        ) : (
          rows.map((r) => (
            <tr key={r.item_no} className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
              <td className="px-3 py-2 whitespace-nowrap text-amp-ink dark:text-amp-dark-ink">
                {r.item_no}
                {r.descr && (
                  <span className="ml-1 font-normal text-amp-ink-muted dark:text-amp-dark-ink-muted">
                    {r.descr}
                  </span>
                )}
              </td>
              <td className="px-3 py-2 whitespace-nowrap text-amp-ink-muted dark:text-amp-dark-ink-muted">
                {r.subcategory ?? '—'}
              </td>
              <MetricCells ty={r.ty} ly={r.ly} decline={r.decline} comp={r.comp} />
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function RetailLeakageInner() {
  const { user } = useSession();
  const perms = user?.groups ?? [];
  const canView = canViewLeakage(perms, user?.superAdmin);

  const [months, setMonths] = useState<number>(DEFAULT_LEAKAGE_MONTHS);
  const [category, setCategory] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const {
    asOf,
    window: leakWindow,
    total: catTotal,
    categories,
    isLoading: catLoading,
    error: catError,
  } = useLeakageCategories(canView ? months : 0, canView);
  const {
    total: skuTotal,
    skus,
    isLoading: skuLoading,
    error: skuError,
  } = useLeakageSkus(canView ? months : 0, canView ? category : null);

  async function handleExport() {
    setExporting(true);
    setToast(null);
    try {
      await downloadLeakageXlsx(months, category);
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  if (!user && !catLoading) return null; // AuthGate handles unauthenticated state

  if (user && !canView) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Revenue Leakage</h1>
        <AccessDeniedCard />
      </div>
    );
  }

  const inDrill = category !== null;
  // The category-level response drives the "not available yet" state: the
  // backend returns as_of = null when it has no data. A drilled category with
  // no SKUs shows its own empty row inside the grid instead.
  const showNotAvailable = !inDrill && !catLoading && asOf === null;

  const count = inDrill ? skus.length : categories.length;
  const countNoun = inDrill ? 'SKUs' : 'categories';
  const viewTotal = inDrill ? skuTotal : catTotal;
  // Categories are drilled by name, so the drill token is already the label.
  const drilledLabel = category;
  // The calendar month the trailing window deliberately excludes because it
  // is still in progress (null until asOf loads).
  const excludedMonth = excludedMonthLabel(asOf);

  return (
    <div className="h-full flex flex-col min-h-0">

      <div className="flex flex-wrap items-end justify-between mb-4 gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Store</p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Revenue Leakage</h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Categories and SKUs ranked by dollar decline in sales versus the same trailing window last
            year, biggest losses first.
            {leakWindow ? (
              <>
                {' '}
                This year {formatWeekDate(leakWindow.ty.start)} to {formatWeekDate(leakWindow.ty.end)} versus
                last year {formatWeekDate(leakWindow.ly.start)} to {formatWeekDate(leakWindow.ly.end)}.
              </>
            ) : null}
          </p>
        </div>
        <div className="flex flex-shrink-0 flex-wrap items-end gap-2">
          <div className="max-w-[220px]">
            <FilterLabel htmlFor="leakage-months">Trailing months</FilterLabel>
            <select
              id="leakage-months"
              value={months}
              onChange={(e) => setMonths(Number(e.target.value))}
              className="w-full border border-amp-line rounded px-2 py-1.5 text-xs bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
            >
              {LEAKAGE_MONTHS_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {leakageMonthsOptionLabel(asOf, m)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
              Complete months only.
              {excludedMonth ? ` ${excludedMonth} is still in progress and is not included.` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void handleExport()}
            disabled={exporting}
            title={
              inDrill
                ? 'Download the SKUs in this category as Excel'
                : 'Download the ranked category list as Excel'
            }
            className="inline-flex items-center gap-1 rounded border border-amp-line px-3 py-1.5 text-xs text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            Export
          </button>
          <ToolPinToggle toolSlug="leakage" toolName="Revenue Leakage" />
        </div>
      </div>

      {toast && (
        <div
          className="shrink-0 mb-4 flex items-center justify-between rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/40 dark:text-red-200"
          role="status"
        >
          {toast}
          <button type="button" onClick={() => setToast(null)} className="ml-4 text-xs">
            &times;
          </button>
        </div>
      )}

      {showNotAvailable ? (
        <NotAvailableCard />
      ) : (
        <DataGridShell
          total={count}
          countNoun={countNoun}
          toolbar={
            <div className="flex flex-wrap items-center justify-between gap-2">
              {inDrill ? (
                <button
                  type="button"
                  onClick={() => setCategory(null)}
                  className="inline-flex items-center gap-1 text-sm text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
                >
                  <ArrowLeft className="h-3 w-3" /> Back to categories
                </button>
              ) : (
                <p className="text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Click a category to see the SKUs driving its decline.
                </p>
              )}
              {viewTotal.decline !== null && (
                <p className="text-[12px] text-amp-ink dark:text-amp-dark-ink">
                  {viewTotal.comp === null ? (
                    <>
                      Total:{' '}
                      <span className="font-semibold text-amp-ink-muted dark:text-amp-dark-ink-muted">
                        {NO_LY_SALES_LABEL}
                      </span>
                    </>
                  ) : (
                    <>
                      Total YoY change:{' '}
                      <span className={`font-semibold ${compColorClass(viewTotal.comp)}`}>
                        {formatYoyChangeAmount(viewTotal.decline)}
                      </span>
                    </>
                  )}
                </p>
              )}
            </div>
          }
        >
          {inDrill ? (
            <>
              <div className="border-b border-amp-line px-3 py-2 text-sm text-amp-ink dark:border-amp-dark-line dark:text-amp-dark-ink">
                <span className="font-semibold">{drilledLabel}</span>
              </div>
              <SkuTable rows={skus} isLoading={skuLoading} error={skuError} />
            </>
          ) : (
            <CategoryTable
              rows={categories}
              isLoading={catLoading}
              error={catError}
              onDrill={(cod) => setCategory(cod)}
            />
          )}
        </DataGridShell>
      )}
    </div>
  );
}

export function RetailLeakage() {
  return (
    <ErrorBoundary>
      <RetailLeakageInner />
    </ErrorBoundary>
  );
}
